import {
  createContext,
  memo,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { z } from 'zod';

const createThemeMQ = () => window.matchMedia('(prefers-color-scheme: dark)');
const ThemeSchema = z.enum(['light', 'dark']);

const LOCAL_STORAGE_KEY = 'theme';

const ThemeContext = createContext<{
  theme: Theme;
  setTheme: (theme: Theme) => void;
} | null>(null);

export type Theme = z.infer<typeof ThemeSchema>;

export type ThemeProviderProps = {
  children: React.ReactNode;

  supportedThemes?: Theme[];
};

export const ThemeProvider = memo(
  ({ children, supportedThemes = ['light', 'dark'] }: ThemeProviderProps) => {
    const { theme, setTheme } = useSetupTheme(supportedThemes);

    return (
      <ThemeContext.Provider
        value={useMemo(() => ({ theme, setTheme }), [theme, setTheme])}
      >
        {children}
      </ThemeContext.Provider>
    );
  }
);

export const useTheme = () => {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
};

const useSetupTheme = (supportedThemes: Theme[]) => {
  if (supportedThemes.length === 0) {
    throw new Error('supportedThemes must be an array of at least one theme');
  }

  const [theme, setTheme] = useState<Theme>(() => {
    try {
      if (supportedThemes.length === 1) {
        return supportedThemes[0];
      }

      const theme = localStorage.getItem(LOCAL_STORAGE_KEY);

      if (!theme) {
        const systemTheme = createThemeMQ().matches ? 'dark' : 'light';
        const supportedTheme = supportedThemes.includes(systemTheme)
          ? systemTheme
          : supportedThemes[0];
        localStorage.setItem(LOCAL_STORAGE_KEY, supportedTheme);

        return supportedTheme;
      }

      return ThemeSchema.parse(theme);
    } catch {
      return createThemeMQ().matches ? 'dark' : 'light';
    }
  });

  useLayoutEffect(() => {
    const mql = createThemeMQ();

    const onChange = (e: MediaQueryListEvent) => {
      const newTheme = e.matches ? 'dark' : 'light';

      if (supportedThemes.includes(newTheme)) {
        setTheme(newTheme);
      }
    };

    mql.addEventListener('change', onChange);

    return () => {
      mql.removeEventListener('change', onChange);
    };
  }, []);

  useLayoutEffect(() => {
    const fn = (e: StorageEvent) => {
      if (e.key === LOCAL_STORAGE_KEY) {
        const newTheme = e.newValue as Theme;

        if (supportedThemes.includes(newTheme)) {
          setTheme(newTheme);
        }
      }
    };

    window.addEventListener('storage', fn);

    return () => {
      window.removeEventListener('storage', fn);
    };
  }, []);

  useLayoutEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);

    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, theme);
    } catch {
      // pass
    }
  }, [theme]);

  return { theme, setTheme };
};
